import { Crown, Sparkles, MapPin } from "lucide-react";
import { getRestaurantImage } from "@/lib/images";
import SmartImage from "@/components/ui/SmartImage";

function formatDistance(m) {
  if (m == null) return "";
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

/**
 * Horizontal-scrolling carousel of featured (paid) sponsorship listings near
 * the user. Rendered above the main restaurant list. Hidden when there are
 * no featured items within the radius.
 *
 * Props:
 *  - restaurants: full result list (already enriched with `featured` + `distance_m`)
 *  - radiusM: max distance from current center to include (default 5000)
 *  - onCardClick(placeId): open details for this restaurant
 */
export default function FeaturedNearby({ restaurants, radiusM = 5000, onCardClick }) {
  const featured = (restaurants || [])
    .filter(
      (r) =>
        r.featured && (r.distance_m == null || r.distance_m <= radiusM)
    )
    .slice(0, 8);

  if (featured.length === 0) return null;

  return (
    <section
      data-testid="featured-nearby"
      className="relative -mx-6 px-6 pt-1 pb-3 mb-2"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-[#E1A624]" />
          <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#E1A624]">
            Featured near you
          </span>
        </div>
        <span className="text-[10px] text-[#8A9A8B]">
          {featured.length} {featured.length === 1 ? "spot" : "spots"}
        </span>
      </div>

      <div
        data-testid="featured-nearby-scroller"
        className="flex gap-3 overflow-x-auto no-scrollbar pb-2 -mr-6 pr-6 snap-x snap-mandatory"
      >
        {featured.map((r, i) => (
          <FeaturedCard
            key={r.place_id}
            r={r}
            index={i}
            onClick={() => onCardClick?.(r.place_id)}
          />
        ))}
      </div>
    </section>
  );
}

function FeaturedCard({ r, onClick, index = 0 }) {
  const photo = getRestaurantImage(r);
  return (
    <button
      type="button"
      data-testid={`featured-card-${r.place_id}`}
      onClick={onClick}
      className="group relative shrink-0 w-44 snap-start rounded-2xl overflow-hidden border-2 border-[#E1A624] bg-white text-left shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
    >
      <div className="relative h-24 overflow-hidden bg-[#F2EDE3]">
        <SmartImage
          src={photo}
          alt={r.name}
          // First 3 featured cards are visible without horizontal scroll
          // on mobile — load them eagerly.
          priority={index < 3}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 h-5 rounded-full text-[9px] font-bold bg-gradient-to-r from-[#E1A624] to-[#D95D39] text-white shadow">
          <Crown className="w-2.5 h-2.5" />
          Featured
        </span>
      </div>
      <div className="p-2.5">
        <h3 className="font-display text-sm font-medium text-[#2A3B2C] line-clamp-1">
          {r.name}
        </h3>
        <div className="mt-1 flex items-center gap-1 text-[10px] text-[#647466]">
          <MapPin className="w-2.5 h-2.5 text-[#D95D39] shrink-0" />
          <span className="truncate">
            {r.distance_m != null ? formatDistance(r.distance_m) : "Nearby"}
            {r.primary_type ? ` · ${r.primary_type.replace(/_/g, " ")}` : ""}
          </span>
        </div>
      </div>
    </button>
  );
}
